import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { logInteraction } from '../../../services/interactions.js';

/**
 * Log a call or a meeting after the fact (CT-5724). One save per double click
 * (`submittingRef`); a toast either way; `onLogged` on success (the layout closes
 * the form and the Timeline refreshes). On failure the form stays with what the
 * user typed.
 */
export function useLogInteraction({ entityType, entityId, onLogged } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const submittingRef = useRef(false);

  const log = useCallback(
    async (kind, values) => {
      if (submittingRef.current) return false;
      submittingRef.current = true;
      setSaving(true);

      try {
        await logInteraction({ kind, entityType, entityId, ...values });
        toast.success({ title: t(`history.log.${kind}.toastTitle`), message: t('history.log.toastMessage') });
        onLogged?.();
        return true;
      } catch (err) {
        toast.error({ title: t('history.log.errorTitle'), message: err?.message ?? t('history.log.errorMessage') });
        return false;
      } finally {
        submittingRef.current = false;
        setSaving(false);
      }
    },
    [entityType, entityId, onLogged, toast, t],
  );

  return useMemo(() => ({ log, saving }), [log, saving]);
}

export default useLogInteraction;
